import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  Video,
  User,
  Mail,
  Phone,
  Building2,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  MessageSquare,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const MEETING_TYPES = [
  { id: 'demo', label: 'Product Demo', duration: 30, description: 'See MedexOVA in action' },
  { id: 'consultation', label: 'Consultation', duration: 45, description: 'Discuss your NEMT needs' },
  { id: 'followup', label: 'Follow-up Call', duration: 15, description: 'Quick check-in' }
];

export const PublicScheduler = () => {
  const [step, setStep] = useState(1); // 1: meeting type, 2: date/time, 3: details, 4: confirmed
  const [meetingType, setMeetingType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [confirmedEvent, setConfirmedEvent] = useState(null);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_company: '',
    notes: ''
  });
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Fetch available slots
  useEffect(() => {
    if (meetingType) {
      const fetchSlots = async () => {
        setLoading(true);
        try {
          const duration = MEETING_TYPES.find(m => m.id === meetingType)?.duration || 30;
          const response = await fetch(`${API_URL}/api/calendar/public/slots?duration=${duration}&days=30`);
          const data = await response.json();
          setSlots(data.slots_by_date || {});
        } catch (error) {
          console.error('Failed to fetch slots:', error);
        }
        setLoading(false);
      };
      fetchSlots();
    }
  }, [meetingType, API_URL]);
  
  // Get days in month for calendar
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month padding
    for (let i = 0; i < startingDay; i++) {
      days.push({ date: null, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      const dateStr = dayDate.toISOString().split('T')[0];
      days.push({
        date: dayDate,
        dateStr,
        isCurrentMonth: true,
        hasSlots: slots[dateStr]?.length > 0
      });
    }
    
    return days;
  };
  
  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  
  // Handle booking submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot || !formData.guest_name || !formData.guest_email) {
      return;
    }
    
    setBooking(true);
    
    try {
      const response = await fetch(`${API_URL}/api/calendar/public/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slot_start: selectedSlot.start,
          slot_end: selectedSlot.end,
          meeting_type: meetingType,
          ...formData
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConfirmedEvent(data.event);
        setStep(4);
      } else {
        alert(data.detail || 'Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      alert('Booking failed. Please try again.');
    }
    
    setBooking(false);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple flex items-center justify-center">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">MedexOVA</h1>
              <p className="text-sm text-slate-400">Schedule a Demo</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center space-x-4 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${step >= s ? 'bg-neon-cyan text-black' : 'bg-slate-700 text-slate-400'}
              `}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && (
                <div className={`w-12 h-0.5 ${step > s ? 'bg-neon-cyan' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        <AnimatePresence mode="wait">
          {/* Step 1: Meeting Type */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                What would you like to discuss?
              </h2>
              <p className="text-slate-400 text-center mb-8">
                Select the type of meeting that best fits your needs
              </p>
              
              <div className="space-y-4">
                {MEETING_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setMeetingType(type.id);
                      setStep(2);
                    }}
                    className={`
                      w-full p-6 rounded-xl border transition-all text-left
                      ${meetingType === type.id 
                        ? 'bg-neon-cyan/20 border-neon-cyan' 
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{type.label}</h3>
                        <p className="text-sm text-slate-400">{type.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-neon-cyan">{type.duration} min</div>
                        <ArrowRight className="w-5 h-5 text-slate-400 mt-2" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
          
          {/* Step 2: Date & Time */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setStep(1)}
                className="flex items-center text-slate-400 hover:text-white mb-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Select a date & time
              </h2>
              <p className="text-slate-400 text-center mb-8">
                Choose a time that works for you
              </p>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        className="p-2 hover:bg-white/10 rounded-lg"
                      >
                        <ChevronLeft className="w-5 h-5 text-white" />
                      </button>
                      <h3 className="font-medium text-white">
                        {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                      </h3>
                      <button
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        className="p-2 hover:bg-white/10 rounded-lg"
                      >
                        <ChevronRight className="w-5 h-5 text-white" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 mb-2">
                      {DAYS_OF_WEEK.map(day => <div key={day}>{day}</div>)}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((day, idx) => {
                        if (!day.date) {
                          return <div key={idx} className="p-3" />;
                        }
                        
                        const isSelected = selectedDate?.toDateString() === day.date.toDateString();
                        const isPast = day.date < today;
                        const isToday = day.date.toDateString() === today.toDateString();
                        
                        return (
                          <button
                            key={idx}
                            onClick={() => day.hasSlots && !isPast && setSelectedDate(day.date)}
                            disabled={!day.hasSlots || isPast}
                            className={`
                              p-3 text-sm rounded-lg transition-all
                              ${isSelected ? 'bg-neon-cyan text-black font-bold' : ''}
                              ${!isSelected && day.hasSlots && !isPast ? 'text-white hover:bg-white/10' : ''}
                              ${!day.hasSlots || isPast ? 'text-slate-600 cursor-not-allowed' : ''}
                              ${isToday && !isSelected ? 'ring-1 ring-neon-cyan' : ''}
                            `}
                          >
                            {day.date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Time Slots */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="font-medium text-white mb-4 flex items-center">
                      <Clock className="w-4 h-4 mr-2 text-neon-cyan" />
                      {selectedDate 
                        ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                        : 'Select a date'
                      }
                    </h3>
                    
                    {selectedDate ? (
                      <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                        {(slots[selectedDate.toISOString().split('T')[0]] || []).map((slot, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedSlot(slot)}
                            className={`
                              px-4 py-3 rounded-lg text-sm font-medium transition-all
                              ${selectedSlot?.start === slot.start
                                ? 'bg-neon-cyan text-black'
                                : 'bg-white/10 text-white hover:bg-white/20'
                              }
                            `}
                          >
                            {slot.formatted}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                        <p>Select a date to see available times</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedSlot && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setStep(3)}
                    className="btn-primary px-8 py-3 text-lg"
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2 inline" />
                  </button>
                </div>
              )}
            </motion.div>
          )}
          
          {/* Step 3: Contact Details */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-xl mx-auto"
            >
              <button
                onClick={() => setStep(2)}
                className="flex items-center text-slate-400 hover:text-white mb-4"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
              
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Enter your details
              </h2>
              <p className="text-slate-400 text-center mb-8">
                We'll send the meeting details to your email
              </p>
              
              {/* Selected Time Summary */}
              <div className="bg-neon-cyan/10 border border-neon-cyan/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-neon-cyan">
                      {MEETING_TYPES.find(m => m.id === meetingType)?.label}
                    </div>
                    <div className="text-white font-medium">
                      {selectedDate?.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="text-slate-400">{selectedSlot?.formatted}</div>
                  </div>
                  <Calendar className="w-8 h-8 text-neon-cyan" />
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Your Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      value={formData.guest_name}
                      onChange={e => setFormData({ ...formData, guest_name: e.target.value })}
                      placeholder="John Smith"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-neon-cyan focus:outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="email"
                      value={formData.guest_email}
                      onChange={e => setFormData({ ...formData, guest_email: e.target.value })}
                      placeholder="john@company.com"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-neon-cyan focus:outline-none"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Phone (optional)</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="tel"
                        value={formData.guest_phone}
                        onChange={e => setFormData({ ...formData, guest_phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-neon-cyan focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-slate-400 mb-1 block">Company (optional)</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        value={formData.guest_company}
                        onChange={e => setFormData({ ...formData, guest_company: e.target.value })}
                        placeholder="Company Name"
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-neon-cyan focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Notes (optional)</label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                    <textarea
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Anything specific you'd like to discuss?"
                      rows={3}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:border-neon-cyan focus:outline-none resize-none"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={booking}
                  className="w-full btn-primary py-4 text-lg flex items-center justify-center"
                >
                  {booking ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Schedule Meeting
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 4 && confirmedEvent && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-xl mx-auto text-center"
            >
              <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-neon-green" />
              </div>
              
              <h2 className="text-3xl font-bold text-white mb-2">
                You're all set!
              </h2>
              <p className="text-slate-400 mb-8">
                A confirmation has been sent to your email
              </p>
              
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-left mb-6">
                <h3 className="font-semibold text-white mb-4">{confirmedEvent.title}</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center text-slate-300">
                    <Calendar className="w-5 h-5 mr-3 text-neon-cyan" />
                    {new Date(confirmedEvent.start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="flex items-center text-slate-300">
                    <Clock className="w-5 h-5 mr-3 text-neon-cyan" />
                    {new Date(confirmedEvent.start).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                  <div className="flex items-center text-slate-300">
                    <Video className="w-5 h-5 mr-3 text-neon-cyan" />
                    Video Conference
                  </div>
                </div>
                
                {confirmedEvent.meeting_link && (
                  <a
                    href={confirmedEvent.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full btn-primary py-3 text-center"
                  >
                    <Video className="w-4 h-4 mr-2 inline" />
                    Join Meeting
                  </a>
                )}
              </div>
              
              <p className="text-sm text-slate-500">
                Add this meeting to your calendar using the link in your confirmation email.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PublicScheduler;
